import { useMutation, useQuery } from '@apollo/react-hooks';
import { MaterialIcons } from '@expo/vector-icons';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlined from '@material-ui/icons/KeyboardArrowUpOutlined';
import SubjectIcon from '@material-ui/icons/Subject';
import lodash from 'lodash';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { Modal } from '../../../../components';
import { Checkbox, Switch } from '../../../../core-ui';
import {
  GetUpdatedStoreHistory,
  GetUpdatedStoreHistoryVariables,
} from '../../../../generated/GetUpdatedStoreHistory';
import {
  AccessUser,
  DataType,
  PhoneCategory,
  RequestAgentVerificationUpdateInput,
  Role,
  StatusActive,
  StatusType,
  StatusVerify,
  StoreInputData,
  StorePhotoCategory,
  SubUserUpdateInput,
} from '../../../../generated/globalTypes';
import {
  RequestMatchMaking,
  RequestMatchMakingVariables,
} from '../../../../generated/RequestMatchMaking';
import {
  UpdateRequestVerificationStore,
  UpdateRequestVerificationStoreVariables,
} from '../../../../generated/UpdateRequestVerificationStore';
import { VerificationRequests_requestVerificationAdvance_row as Data } from '../../../../generated/VerificationRequests';
import { getFormattedSentenceDate } from '../../../../helpers/formatDate';
import validateEmail from '../../../../helpers/validateEmail';
import {
  CityDropdown,
  DistrictDropdown,
  Dropdown,
  ProvinceDropdown,
  RoleDropdown,
  SegmentedControl,
  StoreCategoryDropdown,
  VillageDropdown,
} from '../../components';
import { UnverifiedDropDownSegment } from '../../components/UnverifiedDropDownSegment';
import { emptyUnverifiedCategories } from '../../constants/categoryUnverified';
import {
  storeValidation as optionsStoreValidation,
  validVisitation as optionsValidVisitation,
} from '../../constants/storeValidation';
import { colors, spacing } from '../../constants/theme';
import { tokoStatus } from '../../constants/tokoStatus';
import { useSnackbar } from '../../context';
import { Button, Text, TextInput } from '../../core-ui';
import { RowContainer } from '../../core-ui/RowContainer';
import { UPDATE_REQUEST_VERIFICATION_STORE } from '../../graphql/mutations';
import {
  GET_UPDATED_STORE_HISTORY,
  REQUEST_MATCHMAKING,
} from '../../graphql/queries';
import { checkPhoneNumberFormat, toPascalCase } from '../../helpers';
import { generateDetailUnverified } from '../../helpers/generateDetailUnverified';

import { Collapsible } from './Collapsible';
import TokoCollapsedItem from './TokoCollapsedItem';
import {
  EmployeeStoreLogChanges,
  StoreLogChangesRespond,
  UnverifiedCategory,
} from './types';

const nameRegex = /^[a-zA-Z\s]+$/;

type Props = {
  onClose: () => void;
  data: Data;
  viewMode: boolean;
  refetchData: () => void;
};

type MatchMaking = RequestAgentVerificationUpdateInput & {
  brandName: string;
  selectedDistributorName: string;
  agentSelectName?: string;
};

type FormEmployee = Omit<SubUserUpdateInput, 'status'> & {
  status: StatusActive | null;
};

type StoreData = {
  storePhoneNumber: string;
  storeCategoryId: string;
  villageId: string;
  districtId: string;
  cityId: string;
  provinceId: string;
  rt: string;
  rw: string;
  postalCode?: string | null;
  storeName?: string | null;
  storeEmail?: string | null;
  address?: string | null;
  dataType?: DataType | null;
};

const TokoSuperEditForm = memo<Props>(
  ({ viewMode, data, refetchData, onClose }) => {
    let currentStoreData = data.store;

    const [storeData, setStoreData] = useState<StoreData>({
      provinceId: currentStoreData?.storeManagerBulk?.provinceId ?? '',
      cityId: currentStoreData?.storeManagerBulk?.cityId ?? '',
      districtId: currentStoreData?.storeManagerBulk?.districtId ?? '',
      villageId: currentStoreData?.storeManagerBulk?.villageId ?? '',
      rt: currentStoreData?.rt ?? '00',
      rw: currentStoreData?.rw ?? '00',
      storeCategoryId:
        currentStoreData?.storeManagerBulk?.storeCategoryId ?? '',
      storePhoneNumber:
        currentStoreData?.storeManagerBulk?.storePhoneNumber ?? '',
      storeName: currentStoreData?.storeManagerBulk?.storeName,
      postalCode: currentStoreData?.storeManagerBulk?.postalCode,
      address: currentStoreData?.storeManagerBulk?.address,
      storeEmail: currentStoreData?.storeManagerBulk?.storeEmail,
      dataType:
        currentStoreData?.storeManagerBulk?.dataType ||
        currentStoreData?.dataType,
    });

    const [employees, setEmployees] = useState<Array<FormEmployee>>(
      currentStoreData?.User?.map(
        ({ id, profile, role, accessUser, status }) => ({
          id: id,
          name: profile?.name || '',
          role: role,
          email: profile?.email || '',
          privileges: accessUser,
          status: status,
        }),
      ) ?? [],
    );

    let ownerPhone = useMemo(() => {
      return (
        currentStoreData?.User?.find((user) => user.role === Role.TOKO_OWNER)
          ?.phoneNumber ?? ''
      );
    }, [currentStoreData]);

    const { openSnackbar } = useSnackbar();

    const [submitError, setSubmitError] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState<'FORM' | 'RIWAYAT'>('FORM');
    const [unverifiedCategories, setUnverifiedCategories] = useState<
      Array<UnverifiedCategory>
    >(
      data.unverifiedReasonData?.map(
        ({
          nextAction,
          problemReason,
          otherReason,
          storePhotoCategory,
          phoneCategory,
        }) => ({
          type: !!storePhotoCategory ? 'Foto' : 'Telepon',
          detail: generateDetailUnverified(phoneCategory ?? storePhotoCategory),
          problem: otherReason ? 'Lainnya' : problemReason,
          customProblem: otherReason || '',
          action: nextAction || '',
        }),
      ) ?? [emptyUnverifiedCategories],
    );

    const [storeStatus, setStoreStatus] = useState<boolean>(
      Boolean(
        currentStoreData?.User?.some(
          (user) => user.status === StatusActive.ACTIVE,
        ),
      ),
    );

    let { data: requestAgentData, loading: requestAgentLoading } = useQuery<
      RequestMatchMaking,
      RequestMatchMakingVariables
    >(REQUEST_MATCHMAKING, {
      variables: { storeId: data.storeId ?? '', isVerified: viewMode },
      notifyOnNetworkStatusChange: true,
    });

    const { data: historyData } = useQuery<
      GetUpdatedStoreHistory,
      GetUpdatedStoreHistoryVariables
    >(GET_UPDATED_STORE_HISTORY, {
      variables: { verificationId: data.id },
    });

    const [updateStoreData, { loading }] = useMutation<
      UpdateRequestVerificationStore,
      UpdateRequestVerificationStoreVariables
    >(UPDATE_REQUEST_VERIFICATION_STORE, {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        setSubmitError(false);
        openSnackbar({
          variant: 'success',
          message: t(['Data telah diperbarui.', 'Data has been updated.']),
        });
        refetchData();
        closeModal();
        onClose();
      },
      onError: () => {
        setSubmitError(true);
      },
    });

    const setUpdateStoreData = <K extends keyof typeof storeData>(
      key: K,
      value: typeof storeData[K],
    ) => {
      let newData = { ...storeData, [key]: value };
      setStoreData(newData);
    };

    const openModal = useCallback(() => {
      setModalOpen(true);
    }, []);
    const closeModal = useCallback(() => {
      setModalOpen(false);
    }, []);

    let isStoreDataChanged = (): boolean => {
      let managerStoreData = currentStoreData?.storeManagerBulk;
      let isDataChanged = (Object.entries(storeData) as Array<
        [keyof StoreData, StoreData[keyof StoreData]]
      >).map(([key, value]) => {
        if (managerStoreData && managerStoreData.hasOwnProperty(key)) {
          return {
            key,
            changed:
              managerStoreData[key as keyof typeof managerStoreData] !== value,
          };
        } else if (currentStoreData && currentStoreData.hasOwnProperty(key)) {
          return {
            key,
            changed:
              currentStoreData[key as keyof typeof currentStoreData] !== value,
          };
        } else {
          return {
            key,
            changed: false,
          };
        }
      });
      return isDataChanged.some(({ changed }) => changed);
    };

    let isEmployeesDataChanged = (): boolean => {
      return employees.some(({ name, privileges, status, id: userId }) => {
        let currentUser = data.store?.User?.find(({ id }) => id === userId);

        return (
          name !== currentUser?.profile?.name ||
          privileges.length !== currentUser?.accessUser.length ||
          !lodash.isEmpty(lodash.xor(privileges, currentUser?.accessUser)) ||
          status !== currentUser?.status
        );
      });
    };

    let checkStoreDataInvalid = () => {
      let requiredDataList = [
        storeData.storeName,
        storeData.storePhoneNumber,
        storeData.storeCategoryId,
        storeData.cityId,
        storeData.districtId,
        storeData.postalCode,
        storeData.address,
        storeData.villageId,
        storeData.provinceId,
      ];

      return (
        requiredDataList.some((value) => !value?.trim()) ||
        !checkPhoneNumberFormat({
          phone: storeData.storePhoneNumber,
          skip: ['prefix'],
        }).valid ||
        (!!storeData.storeEmail && !validateEmail(storeData.storeEmail))
      );
    };

    let isDisabled = (): boolean => {
      let isStoreChanged = isStoreDataChanged();
      let isStoreDataInvalid = false;
      if (isStoreChanged) {
        isStoreDataInvalid = checkStoreDataInvalid();
      }

      let isEmployeeChanged = isEmployeesDataChanged();
      let isEmployeesInvalid = false;
      if (isEmployeeChanged) {
        isEmployeesInvalid = !employees.every((employee) =>
          nameRegex.test(employee.name),
        );
      }

      return (
        (!isEmployeeChanged && !isStoreChanged) ||
        (isEmployeeChanged && isEmployeesInvalid) ||
        (isStoreChanged && isStoreDataInvalid)
      );
    };

    let disabledInput = useMemo(
      () => data.status === StatusVerify.REVALIDATION,
      [data.status],
    );

    let onChangeRTRW = (value: string) => {
      const regex = /^\d{0,2}$/;
      if (regex.test(value)) {
        return value;
      }
    };

    let onChangeEmployeeData = <K extends keyof FormEmployee>(
      idx: number,
      property: K,
      value: FormEmployee[K],
    ) => {
      let newEmployee = employees.map((e, index) =>
        idx === index ? { ...e, [property]: value } : e,
      );
      setEmployees(newEmployee);
    };

    const storeStatusOptions = tokoStatus;
    const storeStatusOptionTexts = tokoStatus.map(toPascalCase);

    let updateData = () => {
      let { villageId, address, dataType, ...restData } = storeData;
      const storeDataInput: StoreInputData = {
        ...restData,
        storeAddress: address,
        urbanVillageId: villageId,
        latitude: currentStoreData?.storeManagerBulk?.latitude,
        longitude: currentStoreData?.storeManagerBulk?.longitude,
        website: currentStoreData?.website,
        employee: employees.map((employee) => ({
          ...employee,
          status:
            employee.status === StatusActive.ACTIVE
              ? StatusType.ACTIVE
              : StatusType.INACTIVE,
        })),
        storeDataType: dataType,
      };

      let unverifiedReason = unverifiedCategories.map(
        ({ problem, detail, action, customProblem }) => {
          return {
            nextAction: action,
            phoneType:
              detail === 'Telepon diangkat'
                ? PhoneCategory.ANSWERED
                : detail === 'Telepon tidak diangkat'
                ? PhoneCategory.NOT_ANSWERED
                : null,
            storePhotoType:
              detail === 'Foto Tampak Depan'
                ? StorePhotoCategory.STORE_PHOTO_FRONT
                : detail === 'Foto Bagian Dalam'
                ? StorePhotoCategory.STORE_PHOTO_INSIDE
                : null,
            reason: problem === 'Lainnya' ? null : problem,
            otherReason: problem === 'Lainnya' ? customProblem : null,
          };
        },
      );

      updateStoreData({
        variables: {
          verificationId: data.id,
          status: null,
          existingStoreCode:
            storeStatusOptions.findIndex((status) => status === data.status) ===
            2
              ? currentStoreData?.storeManagerBulk?.storeCode
              : undefined,
          requestAgentData: {},
          storeData: storeDataInput,
          unverifiedReason,
          note: data.note,
        },
        refetchQueries: [
          {
            query: GET_UPDATED_STORE_HISTORY,
            variables: { verificationId: data.id },
          },
        ],
      });
    };

    let {
      storePhoneNumber,
      storeName,
      storeCategoryId,
      storeEmail,
      provinceId,
      cityId,
      districtId,
      postalCode,
      address,
      villageId,
      rt,
      rw,
    } = storeData;

    let storePhoneNumberErrorText = () =>
      checkPhoneNumberFormat({ phone: storePhoneNumber, skip: ['prefix'] })
        .reason ?? '';

    let errorText = storePhoneNumberErrorText();

    return (
      <>
        <View style={styles.root}>
          <View style={styles.header}>
            <View style={styles.row}>
              <TouchableOpacity
                data-testid="list-daftar-data"
                style={[
                  styles.tab,
                  selectedTab === 'FORM' ? styles.selectedTab : {},
                ]}
                onPress={() => setSelectedTab('FORM')}
              >
                <Text
                  color={selectedTab === 'FORM' ? 'black' : 'grey'}
                  bold
                  size="m"
                >
                  Form
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                data-testid="list-dalam-masa-pengerjaan"
                style={[
                  styles.tab,
                  selectedTab === 'RIWAYAT' ? styles.selectedTab : {},
                ]}
                onPress={() => setSelectedTab('RIWAYAT')}
              >
                <Text
                  color={selectedTab === 'RIWAYAT' ? 'black' : 'grey'}
                  bold
                  size="m"
                >
                  Riwayat
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {selectedTab === 'FORM' ? (
            <View>
              <SectionContainer>
                <Text bold size="m" style={styles.lBottomSpacing}>
                  Request Matchmaking
                </Text>

                <View>
                  <View style={styles.row}>
                    <View style={styles.flex}>
                      <Text bold style={styles.xxsBottomSpacing}>
                        Nama Brand
                      </Text>
                    </View>
                    <View style={styles.flex}>
                      <Text bold style={styles.xxsBottomSpacing}>
                        Agent Name (Distributor)
                      </Text>
                    </View>
                  </View>
                  {requestAgentLoading || !requestAgentData ? (
                    <View />
                  ) : (
                    requestAgentData.requestMatchMaking.map((m, i) => (
                      <View style={styles.row} key={i}>
                        <View style={styles.flex}>
                          <Text>{m.brand.name || '-'}</Text>
                        </View>
                        <View style={styles.flex}>
                          <Text>{m.agentName || '-'}</Text>
                        </View>
                      </View>
                    ))
                  )}
                </View>
              </SectionContainer>

              <SectionContainer>
                <RowContainer>
                  <Text bold size="m" style={styles.flex}>
                    Informasi Toko
                  </Text>
                  <Text
                    bold
                    size="m"
                    style={[styles.flex, styles.textAlignRight]}
                  >
                    {currentStoreData?.storeManagerBulk?.storeCode}
                  </Text>
                </RowContainer>

                <TextInput
                  containerStyle={styles.lBottomSpacing}
                  value={storeName}
                  onChangeText={(text) => {
                    setUpdateStoreData('storeName', text);
                  }}
                  label="Nama Toko"
                  required
                  disabled={disabledInput}
                />

                <RowContainer>
                  <TextInput
                    style={{ width: 250 }}
                    value={storePhoneNumber}
                    onChangeText={(phoneNumber) => {
                      setUpdateStoreData('storePhoneNumber', phoneNumber);
                    }}
                    error={
                      storePhoneNumber
                        ? !checkPhoneNumberFormat({
                            phone: storePhoneNumber,
                            skip: ['prefix'],
                          }).valid
                        : false
                    }
                    label="Telepon Toko"
                    helperText={errorText}
                    required
                    disabled={disabledInput}
                  />
                  <TextInput
                    style={{ width: 250 }}
                    value={ownerPhone}
                    onChangeText={() => {}}
                    label="Telepon Pengguna"
                    disabled
                  />
                </RowContainer>

                <StoreCategoryDropdown
                  containerStyle={styles.lBottomSpacing}
                  value={storeCategoryId || ''}
                  onChange={(value) =>
                    setUpdateStoreData('storeCategoryId', value)
                  }
                  label="Kategori Toko"
                  required
                  disabled={disabledInput}
                />

                <View style={styles.lBottomSpacing}>
                  <TextInput
                    value={storeEmail}
                    onChangeText={(text) => {
                      setUpdateStoreData('storeEmail', text);
                    }}
                    label="Email Toko (opsional)"
                    disabled={disabledInput}
                  />
                  {!!storeEmail && !validateEmail(storeEmail) && (
                    <Text size={'xs'} color="red">
                      Tolong gunakan format email address yang benar
                    </Text>
                  )}
                </View>

                <TextInput
                  containerStyle={styles.lBottomSpacing}
                  value={currentStoreData?.website}
                  onChangeText={() => {}}
                  label="Situs (opsional)"
                  disabled
                />

                <TextInput
                  containerStyle={styles.lBottomSpacing}
                  value={
                    data.validVisitation
                      ? `${data.validasiToko} | ${data.validVisitation}`
                      : data.validasiToko
                  }
                  onChangeText={() => {}}
                  label="Kategori Validasi (opsional)"
                  disabled
                />

                <RowContainer>
                  <Dropdown
                    containerStyle={{ width: 250 }}
                    onChange={() => {}}
                    label="Validasi Toko"
                    value={data.validasiToko}
                    options={optionsStoreValidation}
                    disabled
                  />
                  <Dropdown
                    containerStyle={{ width: 250 }}
                    onChange={() => {}}
                    label="Valid Visitation"
                    value={data.validVisitation}
                    options={optionsValidVisitation}
                    disabled
                  />
                </RowContainer>
              </SectionContainer>

              <SectionContainer>
                <Text bold size="m" style={styles.lBottomSpacing}>
                  Alamat
                </Text>

                <TextInput
                  containerStyle={styles.lBottomSpacing}
                  value={address}
                  onChangeText={(text) => {
                    setUpdateStoreData('address', text);
                  }}
                  label="Alamat"
                  required
                  disabled={disabledInput}
                />

                <RowContainer>
                  <ProvinceDropdown
                    containerStyle={styles.w30}
                    label="Provinsi"
                    value={provinceId}
                    onChange={(value) =>
                      setUpdateStoreData('provinceId', value)
                    }
                    required
                    disabled={disabledInput}
                  />
                  <CityDropdown
                    containerStyle={styles.w30}
                    label="Kabupaten/Kota"
                    value={cityId}
                    provinceID={provinceId}
                    onChange={(value) => setUpdateStoreData('cityId', value)}
                    required
                    disabled={disabledInput}
                  />
                  <DistrictDropdown
                    containerStyle={styles.w30}
                    label="Kecamatan"
                    value={districtId}
                    cityID={cityId}
                    onChange={(value) =>
                      setUpdateStoreData('districtId', value)
                    }
                    required
                    disabled={disabledInput}
                  />
                </RowContainer>

                <RowContainer>
                  <VillageDropdown
                    containerStyle={styles.w30}
                    label="Kelurahan"
                    value={villageId}
                    districtID={districtId}
                    onChange={(value) => setUpdateStoreData('villageId', value)}
                    required
                    disabled={disabledInput}
                  />
                  <TextInput
                    containerStyle={styles.w30}
                    value={postalCode}
                    onChangeText={(text) =>
                      setUpdateStoreData('postalCode', text)
                    }
                    label="Kode Pos"
                    required
                    disabled={disabledInput}
                  />
                  <TextInput
                    containerStyle={styles.w15}
                    value={rt}
                    onChangeText={(text) =>
                      setUpdateStoreData('rt', onChangeRTRW(text) ?? rt)
                    }
                    label="RT"
                    disabled={disabledInput}
                  />
                  <TextInput
                    containerStyle={styles.w15}
                    value={rw}
                    onChangeText={(text) =>
                      setUpdateStoreData('rw', onChangeRTRW(text) ?? rw)
                    }
                    label="RW"
                    disabled={disabledInput}
                  />
                </RowContainer>

                <RowContainer style={{ alignItems: 'flex-end' }}>
                  <TextInput
                    containerStyle={styles.w30}
                    value={currentStoreData?.storeManagerBulk?.longitude}
                    onChangeText={() => {}}
                    label="Longitude"
                    disabled
                  />

                  <TextInput
                    containerStyle={styles.w30}
                    value={currentStoreData?.storeManagerBulk?.latitude}
                    onChangeText={() => {}}
                    label="Latitude"
                    disabled
                  />

                  <TouchableOpacity style={styles.mapsButton}>
                    <MaterialIcons
                      name="map"
                      size={24}
                      color={colors.icon.blue}
                    />
                    <Text size="s" style={styles.labelMapsButton}>
                      View on Maps
                    </Text>
                  </TouchableOpacity>
                </RowContainer>
              </SectionContainer>

              <SectionContainer>
                <Text bold size="m" style={styles.lBottomSpacing}>
                  {t(['Informasi Karyawan', 'Request Matchmaking'])}
                </Text>

                {employees?.map((employee, i) => (
                  <EmployeeInfoCard
                    key={i}
                    {...employee}
                    onChangeProperty={(property, value) =>
                      onChangeEmployeeData(i, property, value)
                    }
                  />
                ))}
              </SectionContainer>

              <SectionContainer>
                <RowContainer>
                  <View style={{ flex: 5 }}>
                    <Text bold size="m">
                      {t(['Status Toko', 'Request Matchmaking'])}
                    </Text>
                    <Text size="s">
                      Cek status TOKO di Manager:{' '}
                      <Text
                        style={{ color: colors.text.blue }}
                        onPress={() => {}}
                      >
                        https://mngr.dbo.id/admin/stores
                      </Text>
                    </Text>
                  </View>

                  <View style={[styles.flex, styles.switchContainer]}>
                    <Text bold style={styles.switchText}>
                      {storeStatus
                        ? t(['Aktif', 'Active'])
                        : t(['Tidak Aktif', 'Non Active'])}
                    </Text>
                    <Switch
                      checked={storeStatus}
                      onPress={(value) => {
                        setStoreStatus(value);
                        const newEmployee = employees.map((e) => ({
                          ...e,
                          status: value
                            ? StatusActive.ACTIVE
                            : StatusActive.INACTIVE,
                        }));
                        setEmployees(newEmployee);
                      }}
                    />
                  </View>
                </RowContainer>

                <SegmentedControl
                  data-testid="owner-verification-status"
                  containerStyle={styles.w100}
                  selected={storeStatusOptions.findIndex(
                    (status) => status === toPascalCase(data.status),
                  )}
                  setSelected={() => {}}
                  options={storeStatusOptionTexts}
                  disabled
                />
              </SectionContainer>

              <SectionContainer>
                <Text bold size="m" style={styles.lBottomSpacing}>
                  Kategori Unverified
                  <Text bold size="s" color="red">
                    *
                  </Text>
                </Text>

                <UnverifiedDropDownSegment
                  data={unverifiedCategories}
                  viewMode={viewMode}
                />

                <Text
                  color={
                    unverifiedCategories.length < 3 && !viewMode
                      ? 'blue'
                      : 'grey'
                  }
                  onPress={() => {
                    if (unverifiedCategories.length < 3 && !viewMode) {
                      setUnverifiedCategories([
                        ...unverifiedCategories,
                        emptyUnverifiedCategories,
                      ]);
                    }
                  }}
                >
                  + Tambah Kategori
                </Text>
              </SectionContainer>

              <SectionContainer>
                <View>
                  <Text bold size="m" style={styles.lBottomSpacing}>
                    Tipe Data Toko
                  </Text>
                  <Dropdown
                    value={storeData.dataType || DataType.REAL}
                    onChange={(value: DataType) => {
                      setStoreData({ ...storeData, dataType: value });
                    }}
                    options={Object.values(DataType).map((value) => ({
                      value,
                      label: value,
                    }))}
                    required
                  />
                </View>
              </SectionContainer>

              <SectionContainer>
                <TextInput
                  data-testid="filter-text-status"
                  containerStyle={!viewMode && styles.lBottomSpacing}
                  value={data.note}
                  onChangeText={() => {}}
                  leftNode={
                    <SubjectIcon
                      htmlColor={colors.border.primary}
                      style={{
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    />
                  }
                  placeholder="Data Valid"
                  disabled
                  multiline
                  rows={3}
                />
              </SectionContainer>
              {submitError && (
                <Text style={{ color: colors.text.red }}>
                  {t([
                    'Gagal mengubah data, lengkapi data" dan coba lagi',
                    'Failed to change the phone number. Please try again later.',
                  ])}
                </Text>
              )}

              <RowContainer style={{ justifyContent: 'flex-end' }}>
                <Button
                  style={styles.buttonWidth}
                  preset="primary"
                  title={t(['Simpan', 'Save'])}
                  onPress={openModal}
                  disabled={isDisabled()}
                />
              </RowContainer>
            </View>
          ) : (
            <View>
              <SectionContainer>
                {historyData?.getUpdatedStoreHistory.map(
                  ({ currentData, updatedAttributes, createdAt }, idx) => {
                    const changes: StoreLogChangesRespond = JSON.parse(
                      updatedAttributes || '',
                    );

                    let count = 0;
                    for (let key in changes) {
                      if (key === 'employees') {
                        for (let employee of changes[key]) {
                          for (let e in employee) {
                            if (e === 'userId' || e === 'email') {
                              continue;
                            }

                            employee[e as keyof EmployeeStoreLogChanges] &&
                              count++;
                          }
                        }
                      } else {
                        changes[key as keyof StoreLogChangesRespond] && count++;
                      }
                    }

                    return (
                      <View key={idx} style={styles.lBottomSpacing}>
                        <Collapsible
                          onPressClosing={() => {}}
                          onPressOpening={() => {}}
                          title={
                            <HeaderCollapsible
                              collapsed={false}
                              title={getFormattedSentenceDate(
                                new Date(createdAt),
                              )}
                              changes={count}
                            />
                          }
                          titleOpened={
                            <HeaderCollapsible
                              collapsed={true}
                              title={getFormattedSentenceDate(
                                new Date(createdAt),
                              )}
                              changes={count}
                            />
                          }
                        >
                          <TokoCollapsedItem
                            currentData={currentData || ''}
                            updatedData={updatedAttributes || ''}
                          />
                        </Collapsible>
                      </View>
                    );
                  },
                )}
              </SectionContainer>
            </View>
          )}
        </View>
        <View>
          <Modal
            open={modalOpen}
            onClose={closeModal}
            title={t(['Simpan Perubahan', 'Save Changes'])}
            titleSize="xl"
            TextComponent={Text}
          >
            <Text style={styles.bottomContainer}>
              {t([
                'Apakah Anda sudah yakin dengan perubahan ini?',
                'Are you sure with this change?',
              ])}
            </Text>
            <View style={styles.confirmationModalButton}>
              <Button
                title={t(['Tidak', 'No'])}
                preset="secondary"
                onPress={closeModal}
              />
              <Button
                title={t(['Ya', 'Yes'])}
                onPress={updateData}
                isLoading={loading}
              />
            </View>
          </Modal>
        </View>
      </>
    );
  },
);

type EmployeeInfoCardProps = {
  name: string;
  role?: Role | null;
  email?: string | null;
  privileges: Array<AccessUser>;
  status?: StatusActive | null;
  onChangeProperty: (
    property: keyof FormEmployee,
    value: FormEmployee[keyof FormEmployee],
  ) => void;
};

const EmployeeInfoCard = ({
  name,
  role,
  email = '',
  privileges,
  status,
  onChangeProperty,
}: EmployeeInfoCardProps) => {
  let isNameError = !nameRegex.test(name);

  let onPressCheckbox = (permission: AccessUser) => {
    if (privileges.includes(permission)) {
      let newAccess = privileges.filter((a) => a !== permission);
      onChangeProperty('privileges', newAccess);
    } else {
      let newAccess = [...privileges, permission];
      onChangeProperty('privileges', newAccess);
    }
  };

  return (
    <View style={styles.employeeInfo}>
      <View style={[styles.mPaddingHorizontal, styles.mPaddingTop]}>
        <RowContainer style={isNameError ? { marginBottom: 0 } : undefined}>
          <TextInput
            containerStyle={styles.w50}
            value={name}
            onChangeText={(value) => {
              onChangeProperty('name', value);
            }}
            label="Name"
          />
          <RoleDropdown
            containerStyle={styles.w50}
            value={role || ''}
            onChange={(value) => onChangeProperty('role', value)}
            label="Role"
            disabled
          />
        </RowContainer>
        {isNameError && (
          <Text size="xs" color="red" style={styles.lBottomSpacing}>
            Nama tidak boleh berisi nomor atau simbol khusus
          </Text>
        )}

        <TextInput
          containerStyle={styles.lBottomSpacing}
          value={email}
          onChangeText={(value) => onChangeProperty('email', value)}
          label="Email (opsional)"
          disabled
        />

        <Text size="xs" style={styles.formLabel}>
          Permission
        </Text>
        <RowContainer style={{ justifyContent: 'flex-start' }}>
          <View style={styles.flex}>
            <Checkbox
              checked={privileges.includes(AccessUser.CAN_ORDER)}
              onPress={() => onPressCheckbox(AccessUser.CAN_ORDER)}
              labelNode={<Text>Dapat Memesan</Text>}
            />
          </View>

          <View style={styles.flex}>
            <Checkbox
              checked={privileges.includes(AccessUser.ADD_STAFF)}
              onPress={() => onPressCheckbox(AccessUser.ADD_STAFF)}
              labelNode={<Text>Menambah Staff</Text>}
            />
          </View>
        </RowContainer>
      </View>

      <RowContainer style={styles.employeeStatus}>
        <Text>Status Karyawan</Text>
        <View style={styles.switchContainer}>
          <Text bold style={styles.switchText}>
            {status === StatusActive.ACTIVE ? 'Aktif' : 'Tidak Aktif'}
          </Text>
          <Switch
            checked={status === StatusActive.ACTIVE}
            onPress={(check) =>
              onChangeProperty(
                'status',
                check ? StatusActive.ACTIVE : StatusActive.INACTIVE,
              )
            }
          />
        </View>
      </RowContainer>
    </View>
  );
};

const SectionContainer: React.FC = ({ children }) => {
  return <View style={styles.sectionContainer}>{children}</View>;
};

const HeaderCollapsible = ({
  title,
  changes,
  collapsed,
}: {
  title: string;
  changes: number;
  collapsed: boolean;
}) => {
  return (
    <View style={styles.headerContainer}>
      <Text bold>{title}</Text>
      <View style={styles.headerText}>
        <Text>{changes.toString()} perubahan</Text>
        {collapsed ? (
          <KeyboardArrowDownOutlinedIcon />
        ) : (
          <KeyboardArrowUpOutlined />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { width: 529, height: 1000, backgroundColor: colors.background.white },
  row: { flexDirection: 'row' },
  flex: { flex: 1 },
  w100: { width: '100%' },
  w50: { width: '48%' },
  w30: { width: '30%' },
  w15: { width: '15%' },
  ///////////////////////////////////////////////
  header: {
    backgroundColor: colors.background.white,
    flexDirection: 'row',
    height: 40,
    shadowColor: colors.background.shadow,
  },
  tab: {
    justifyContent: 'flex-start',
    height: '100%',
    marginHorizontal: spacing.l,
  },
  selectedTab: {
    borderBottomWidth: 2,
    borderBottomColor: colors.button.background.primary,
  },
  ///////////////////////////////////////////////
  mPaddingHorizontal: {
    paddingHorizontal: spacing.m,
  },
  mPaddingTop: { paddingTop: spacing.m },
  lBottomSpacing: {
    marginBottom: spacing.l,
  },
  xxsBottomSpacing: {
    marginBottom: spacing.xxs,
  },
  textAlignRight: { textAlign: 'right' },
  ///////////////////////////////////////////////
  sectionContainer: {
    paddingTop: spacing.l,
    marginBottom: spacing.m,
    marginHorizontal: spacing.s,
    borderTopWidth: 1,
    borderTopColor: colors.border.primary,
  },
  employeeInfo: {
    borderWidth: 1,
    borderColor: colors.border.primary,
    marginBottom: spacing.m,
  },
  employeeStatus: {
    marginBottom: 0,
    padding: spacing.m,
    borderTopWidth: 1,
    borderTopColor: colors.border.primary,
  },
  formLabel: {
    opacity: 0.8,
  },
  mapsButton: {
    borderWidth: 1,
    borderColor: colors.border.primary,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: '65%',
    width: '30%',
  },
  labelMapsButton: {
    color: colors.text.blue,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  switchText: {
    marginRight: spacing.s,
  },
  buttonWidth: {
    width: 30,
  },
  headerContainer: {
    backgroundColor: colors.background.white,
    borderColor: colors.border.primary,
    padding: 16,
    borderWidth: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
  },
  headerText: { flexDirection: 'row', alignItems: 'center' },
  bottomContainer: {
    marginTop: 24,
    padding: 24,
    width: 529,
    backgroundColor: colors.background.white,
  },
  confirmationModalButton: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    paddingVertical: spacing.m,
    paddingRight: spacing.l,
  },
});

export default TokoSuperEditForm;
